"use client";

import React, { useRef } from "react";
import gsap from "gsap";
import { useGSAP } from "@gsap/react";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { ArrowRight, Box, ShieldAlert, Truck } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
gsap.registerPlugin(useGSAP, ScrollTrigger);
export default function ShippingParts() {
  const containerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const container = containerRef.current;
    if (!container) return;
    const mm = gsap.matchMedia();
    mm.add("(prefers-reduced-motion: no-preference)", () => {
      gsap.set(".spec-plate", {
        y: 12
      });
      gsap.to(".spec-plate", {
        opacity: 1,
        y: 0,
        duration: 0.3,
        ease: "power2.out",
        scrollTrigger: {
          trigger: container,
          start: "top 85%",
          once: true
        }
      });
    });
    mm.add("(prefers-reduced-motion: reduce)", () => {
      gsap.set(".spec-plate", {
        opacity: 1,
        y: 0
      });
    });
    const refresh = () => ScrollTrigger.refresh();
    requestAnimationFrame(refresh);
    if (document.readyState === "complete") refresh();else window.addEventListener("load", refresh, {
      once: true
    });
    document.fonts?.ready.then(refresh);
  }, {
    scope: containerRef
  });
  return <section id="shippingpartsandaccessories" data-nav="light" className="w-full bg-background py-16 text-foreground md:py-24">
      <div ref={containerRef} className="mx-auto max-w-5xl px-4 sm:px-6 lg:px-8">
        {/* Engineered Spec Plate Boundary */}
        <div className="spec-plate relative border border-border bg-card p-6 shadow-xs sm:p-10 md:p-12" style={{
        opacity: 0
      }}>
          {/* Industrial Corner Accents */}
          <div aria-hidden="true" className="absolute top-0 left-0 h-2 w-2 border-t-2 border-l-2 border-foreground" />
          <div aria-hidden="true" className="absolute top-0 right-0 h-2 w-2 border-t-2 border-r-2 border-foreground" />
          <div aria-hidden="true" className="absolute bottom-0 left-0 h-2 w-2 border-b-2 border-l-2 border-foreground" />
          <div aria-hidden="true" className="absolute right-0 bottom-0 h-2 w-2 border-b-2 border-r-2 border-foreground" />

          {/* Technical Header Strip */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-4 font-mono text-xs text-muted-foreground uppercase tracking-wider">
            <div className="flex items-center gap-2">
              <span className="inline-block h-2 w-2 bg-primary" />
              <span>Section: Freight & Dispatch Protocol</span>
            </div>
            <div className="flex items-center gap-4">
              <span>Code: LOG-PART-884</span>
              <span className="hidden sm:inline">Class: Industrial Commercial</span>
            </div>
          </div>

          <div className="mt-8 grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
            {/* Left Column: Heading and Key Specs */}
            <div className="lg:col-span-5">
              <div className="inline-flex items-center gap-2 border border-border bg-muted/50 px-2.5 py-1 font-mono text-xs text-muted-foreground">
                <Truck className="size-3.5 text-primary" />
                <span>Component Logistics</span>
              </div>

              <h2 className="mt-4 font-serif text-3xl font-bold tracking-tight text-foreground uppercase sm:text-4xl">
                Shipping Mechanical Bull Parts &amp; Accessories
              </h2>

              <div className="mt-6 space-y-3 border-t border-border pt-6 font-mono text-xs text-muted-foreground">
                <div className="flex items-start gap-2">
                  <Box className="mt-0.5 size-3.5 shrink-0 text-foreground" />
                  <span>Individual packaging parameters by weight classification</span>
                </div>
                <div className="flex items-start gap-2">
                  <ShieldAlert className="mt-0.5 size-3.5 shrink-0 text-foreground" />
                  <span>Motor units and consoles ship via dedicated freight carrier</span>
                </div>
              </div>
            </div>

            {/* Right Column: Required Explanatory Copy & CTA */}
            <div className="flex flex-col justify-between border-t border-border pt-6 lg:col-span-7 lg:border-t-0 lg:border-l lg:pt-0 lg:pl-10">
              <div className="space-y-4 text-base leading-relaxed text-muted-foreground">
                <p>
                  Shipping depends on the component. Small replacement items may
                  travel differently from controllers, blowers or large
                  inflatable arenas. Dimensions, weight, destination and packaging
                  can all affect the delivery method.
                </p>
                <p>
                  Before ordering a large part, confirm what is included and how
                  it will be packed. Replacement inflatables and larger
                  mechanical components may require more planning than small
                  accessories.
                </p>
                <p>
                  The exact shipping arrangement should be confirmed for the
                  individual product rather than assumed across the entire
                  catalog.
                </p>
              </div>

              <div className="mt-8 pt-4">
                <Button asChild size="lg" className="w-full rounded-none border border-primary bg-primary px-8 py-6 font-mono text-xs font-semibold tracking-wider text-primary-foreground uppercase shadow-none transition-transform active:scale-[0.99] sm:w-auto hover:bg-primary/90">
                  <Link to="/ShippingWarrantyAndOrderingPage" className="inline-flex items-center justify-center gap-3">
                    <span>VIEW SHIPPING INFORMATION</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}